/** C3/W1 wire decoders. No page adopts these seams until its own migration. */
import { ApiError, memberApi } from "./client";

export interface ReasonFix { kind: "field" | "act"; target: string }
export interface CommitReason { message: string; ids: string[]; fix: ReasonFix | null }
export interface BulkOutcome { id: string; reason: CommitReason }
/** `facts`: what the command decided as a whole (the adopter's own object), kept through replay and Check. */
export interface BulkResult { state: "confirmed" | "partial"; succeeded: BulkOutcome[]; failed: BulkOutcome[]; facts?: Record<string, unknown> }
export interface CommitReceipt<T> { action: string; subject_ids: string[]; result: T; recorded_at: string }
export type CommitCheck<T = Record<string, unknown>> =
  | { state: "not_committed" | "pending" }
  | { state: "confirmed" | "partial" | "unknown" | "refused"; receipt: CommitReceipt<T> };

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new TypeError("Expected a record");
  return value as Record<string, unknown>;
}
function text(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) throw new TypeError("Expected nonblank text");
  return value;
}
function ids(value: unknown): string[] {
  if (!Array.isArray(value)) throw new TypeError("Expected record ids");
  return value.map(text);
}

export function decodeReason(value: unknown): CommitReason {
  const item = record(value);
  const message = text(item.message);
  if (/[\u2013\u2014]/u.test(message)) throw new TypeError("Expected a plain sentence");
  let fix: ReasonFix | null = null;
  if (item.fix !== null) {
    const target = record(item.fix);
    if (target.kind !== "field" && target.kind !== "act") throw new TypeError("Expected a field or act");
    fix = { kind: target.kind, target: text(target.target) };
  }
  return { message, ids: ids(item.ids), fix };
}

/** Existing errors without the new shape remain ordinary ApiErrors. */
export function reasonOnError(error: unknown): CommitReason | null {
  if (!(error instanceof ApiError)) return null;
  try { return decodeReason(error.detail); } catch { return null; }
}

export function decodeBulkResult(value: unknown): BulkResult {
  const item = record(value);
  function outcomes(value: unknown): BulkOutcome[] {
    if (!Array.isArray(value)) throw new TypeError("Expected bulk outcomes");
    return value.map((raw) => {
      const outcome = record(raw);
      const id = text(outcome.id);
      const reason = decodeReason(outcome.reason);
      if (!reason.ids.includes(id)) throw new TypeError("Reason does not name its record");
      return { id, reason };
    });
  }
  const succeeded = outcomes(item.succeeded);
  const failed = outcomes(item.failed);
  const all = [...succeeded, ...failed].map((outcome) => outcome.id);
  if (!all.length || new Set(all).size !== all.length) throw new TypeError("Expected distinct bulk ids");
  const state = failed.length ? "partial" : "confirmed";
  if (item.state !== state) throw new TypeError("Bulk state disagrees with its outcomes");
  return item.facts === undefined ? { state, succeeded, failed } : { state, succeeded, failed, facts: record(item.facts) };
}

export function decodeCommitCheck<T = Record<string, unknown>>(
  value: unknown, decodeResult: (value: unknown) => T = record as (value: unknown) => T,
): CommitCheck<T> {
  const item = record(value);
  if (item.state === "pending" || item.state === "not_committed") {
    if ("receipt" in item) throw new TypeError("Unconfirmed Check cannot carry a receipt");
    return { state: item.state };
  }
  if (item.state !== "confirmed" && item.state !== "partial" && item.state !== "unknown" && item.state !== "refused") {
    throw new TypeError("Unknown Check state");
  }
  const receipt = record(item.receipt);
  const recorded_at = text(receipt.recorded_at);
  if (!Number.isFinite(Date.parse(recorded_at))) throw new TypeError("Expected a receipt time");
  const result = record(receipt.result);
  if (item.state === "confirmed" && ["partial", "pending", "unknown", "refused", "not_confirmed", "intent_recorded"].includes(String(result.state))) {
    throw new TypeError("Confirmed Check needs a confirmed result");
  }
  if (item.state === "unknown" || item.state === "refused") {
    const subjects = ids(receipt.subject_ids);
    if (result.state !== item.state || subjects.length !== 1 || "succeeded" in result || "failed" in result) {
      throw new TypeError("Unconfirmed Check needs one matching outcome");
    }
    if (!decodeReason(result.reason).ids.includes(subjects[0])) {
      throw new TypeError("Reason does not name its record");
    }
  }
  if (item.state === "partial") {
    const result = record(receipt.result);
    if (result.state !== "partial") throw new TypeError("Partial Check needs a partial result");
    // A bulk result, or a command over many subjects, names its failed ids; a command over one
    // subject is partial of itself, such as a course folder Drive hasn't made ready (#1159).
    const bulk = ids(receipt.subject_ids).length !== 1 || "succeeded" in result || "failed" in result;
    if (bulk && decodeBulkResult(result).state !== "partial") throw new TypeError("Partial Check needs failed ids");
  }
  return { state: item.state, receipt: { action: text(receipt.action), subject_ids: ids(receipt.subject_ids),
    result: decodeResult(receipt.result), recorded_at } };
}

/** The same key is retained through an uncertain result and Check. */
export function commitOptions(key: string, headers?: HeadersInit): { headers: Headers; cache: "no-store" } {
  if (key.length < 8 || key.length > 200 || key.trim() !== key) throw new TypeError("Invalid commit key");
  const result = new Headers(headers);
  result.set("Idempotency-Key", key);
  return { headers: result, cache: "no-store" };
}

/** Keep the original key and command details; send only ids still failed. */
export function retryOptions(key: string, headers?: HeadersInit): ReturnType<typeof commitOptions> {
  const result = commitOptions(key, headers);
  result.headers.set("Idempotency-Retry", "failed");
  return result;
}

export async function checkCommit<T = Record<string, unknown>>(
  key: string, decodeResult?: (value: unknown) => T,
): Promise<CommitCheck<T>> {
  const result = await memberApi.get<unknown>("/commits/check", commitOptions(key));
  return decodeCommitCheck(result, decodeResult);
}
